/**
 * Flow the year table into real pages, and into bands where it is too wide.
 *
 * SELF-CONTAINED BY CONTRACT, like `fitToPage` and `paginateRoster`: the PDF
 * renderer injects this into the browser by stringifying it, so it must not
 * reference anything outside its own body — no imports, no module constants, no
 * helpers. That constraint buys the thing that matters: the on-screen preview
 * and the printed sheet run the identical algorithm, so the preview cannot lie
 * about where the page breaks.
 *
 * A SECOND paginator rather than a generalization of the roster's, and that is
 * the decision rather than an omission. The contract above is exactly what makes
 * these functions un-shareable: everything must live in one body, so a
 * "parameterised engine" would not be small shared helpers but one large
 * function holding two disjoint algorithms behind a mode flag, stringified in
 * full into both renderers. Two small functions is the honest shape, and the
 * roster — the artifact that must not break — is left untouched.
 *
 * Explicit page boxes rather than CSS pagination, and here the reason is the
 * PREVIEW. A repeating `<thead>` genuinely works on paper, but Chromium
 * paginates inside `page.pdf()`, so the document a preview can see is one
 * unbroken flow with no page break in the DOM. The screen would show one
 * continuous table with one header while the paper carried four pages each with
 * its own — a preview that lies about what comes out of the printer.
 */
export function paginateYearTable(root: HTMLElement): number {
  const flow = root.querySelector<HTMLElement>('.ytable__flow')
  const pages = root.querySelector<HTMLElement>('.ytable__pages')
  const masthead = root.querySelector<HTMLElement>('.ytable__masthead')
  const table = flow?.querySelector<HTMLTableElement>('.ytable__grid')
  if (!flow || !pages || !table) return 0

  const headCells = Array.from(
    table.querySelectorAll('thead th'),
  ) as HTMLElement[]
  const bodyRows = Array.from(
    table.querySelectorAll('tbody tr'),
  ) as HTMLElement[]
  if (headCells.length === 0 || bodyRows.length === 0) return 0

  const identity = Math.max(
    0,
    Math.min(
      Number.parseInt(root.dataset.identity ?? '0', 10) || 0,
      headCells.length - 1,
    ),
  )

  /**
   * How wide the text inside an element actually is.
   *
   * A range around the contents, never `scrollWidth`: that never reports less
   * than the box, so a short value in a wide column and one filling it exactly
   * measure the same, and nothing ever reads as having room to spare.
   */
  const textWidth = (el: HTMLElement) => {
    const range = document.createRange()
    range.selectNodeContents(el)
    const width = range.getBoundingClientRect().width
    range.detach()
    return width
  }

  /*
   * What each column NEEDS: its widest VALUE. The header is not consulted — it
   * wraps to whatever the values leave it.
   *
   * A heading is a sentence and a value is a time, so a column sized to its
   * heading is a column of air: `יש לקרות קריאת שמע קודם שחרית` would take
   * five times the room its `כן` needs, and every column beside it would be
   * squeezed or pushed onto another band to pay for it.
   *
   * Measured from the flow, where the table is laid out at its natural width,
   * so this is the content's own demand rather than what an earlier pass left
   * it with.
   */
  const natural: number[] = headCells.map(() => 0)
  /*
   * And what a single TIME needs — the widest one on the sheet.
   *
   * Every column of times is set to it, so they read as one grid rather than
   * as columns that each shrank to their own longest minute: `10:03am` is wider
   * than `8:45am`, and a sheet whose columns differ by those few pixels looks
   * like a mistake rather than like a measure. A cell holding something else —
   * two opinions side by side, a phrase — is not a time and asks for its own
   * width.
   */
  let timeWidth = 0

  /*
   * Measured with the flow table at its NATURAL width, and this is what makes
   * the numbers mean anything.
   *
   * Laid out as it renders — `fixed`, at 100% of whatever holds it, with no
   * widths on its columns — the browser gives every column an equal share and
   * the text inside wraps to fit. A range around a wrapped cell reports its
   * widest LINE, so the longest parsha measured as the fragment it had been
   * broken into and the column was then sized to hold that fragment: the value
   * wrapped on the page for exactly the reason it had measured narrow.
   */
  const flowLayout = table.style.tableLayout
  const flowWidth = table.style.width
  table.style.tableLayout = 'auto'
  table.style.width = 'max-content'

  for (const row of bodyRows) {
    const cells = Array.from(row.children) as HTMLElement[]
    for (let i = 0; i < cells.length && i < natural.length; i++) {
      const w = textWidth(cells[i])
      if (w > natural[i]) natural[i] = w
      const plainTime =
        i >= identity &&
        cells[i].childElementCount > 0 &&
        !cells[i].querySelector('.ycell__part, .ycell__said')
      if (plainTime && w > timeWidth) timeWidth = w
    }
  }
  // The padding a cell adds around its text, read off the sheet rather than
  // guessed — a guess here is a column that overflows on one theme and not
  // another.
  const pad = (() => {
    const style = getComputedStyle(headCells[0])
    return (
      Number.parseFloat(style.paddingLeft) +
      Number.parseFloat(style.paddingRight) +
      2
    )
  })()

  /* Put the flow back as it renders. It is emptied into the pages below, but
     it is also the whole sheet without script, and that has to stay right. */
  table.style.tableLayout = flowLayout
  table.style.width = flowWidth
  const want = natural.map(
    (w, i) => Math.ceil(i < identity ? w : Math.max(w, timeWidth)) + pad,
  )

  /*
   * The page's usable WIDTH, read off a probe of the real thing — it decides
   * how the columns band, which has to be settled before any page exists. The
   * HEIGHT is not taken here: it is measured on each real page once its
   * masthead and header are in it, which is the only place the answer is exact.
   */
  const { pageWidthPx } = (() => {
    const probe = document.createElement('div')
    probe.className = 'ytable__page'
    probe.style.position = 'absolute'
    probe.style.visibility = 'hidden'
    root.appendChild(probe)
    const style = getComputedStyle(probe)
    const box = probe.getBoundingClientRect()
    const value = {
      pageWidthPx:
        box.width -
        Number.parseFloat(style.paddingLeft) -
        Number.parseFloat(style.paddingRight),
    }
    probe.remove()
    return value
  })()

  /*
   * BANDS — the answer to a column set too wide for the page.
   *
   * Nothing is capped and nothing is dropped: the columns that do not fit start
   * a new band, and the identity columns repeat on each one so every band is
   * readable on its own. Where the split falls is wherever measurement says, so
   * adding a column can never change the outcome by crossing a threshold —
   * there is no threshold.
   */
  const idWidth = want.slice(0, identity).reduce((a, b) => a + b, 0)
  const bands: number[][] = []
  let band: number[] = []
  let bandWidth = idWidth
  for (let i = identity; i < want.length; i++) {
    // A band always takes at least one column, or a single column wider than
    // the page loops for ever.
    if (band.length > 0 && bandWidth + want[i] > pageWidthPx) {
      bands.push(band)
      band = []
      bandWidth = idWidth
    }
    band.push(i)
    bandWidth += want[i]
  }
  if (band.length > 0) bands.push(band)
  if (bands.length === 0) bands.push([])

  /*
   * How tall a row is, measured INSIDE A REAL PAGE BOX.
   *
   * Not in the flow: the flow is as wide as whatever holds it and is not a
   * page, so a row measured there came out ~19.6px and rendered at 21px once
   * placed — three rows a page of slack, which the page then overflowed into
   * its own bottom margin. `overflow: hidden` clips at the BORDER box, so the
   * rows ran straight through the margin rather than being cut at it, and the
   * sheet printed to the edge.
   *
   * The row height is stated in CSS (`--row-h`) precisely so it is one number;
   * measuring a real row in a real page is how this reads that number without
   * restating it here.
   */
  /*
   * How tall a row is: READ FROM `--row-h`, not measured.
   *
   * The height is stated in CSS precisely so it is one number, and measuring a
   * row somewhere it is not yet placed reintroduces the disagreement the
   * statement exists to end — in the flow a row came out ~19.6px and rendered
   * at 21px once placed, three rows a page of slack, which the page then
   * overflowed into its own bottom margin. `overflow: hidden` clips at the
   * BORDER box, so those rows ran through the margin instead of being cut at
   * it, and the sheet printed to the edge.
   */
  const rowHeight =
    Number.parseFloat(getComputedStyle(root).getPropertyValue('--row-h')) || 21

  pages.textContent = ''
  let pageCount = 0

  for (const cols of bands) {
    const indices = [...Array.from({ length: identity }, (_, i) => i), ...cols]

    /*
     * The room the band does not use, split equally between every column that
     * holds a VALUE — a time or a date. Only the row's NAME keeps its own
     * width, and the sheet already draws that line: a date is not a name, which
     * is why it centers with the times instead of reading right like the Hebrew.
     *
     * The sheet still spans its page — a printed table stopping short of the
     * margin reads as one that failed to load — but WHICH columns grow is a
     * decision, and left to the browser it is the wrong one twice over.
     * `width: 100%` over fixed columns shares the slack out in PROPORTION, so
     * the widest column takes the largest share: the parsha rendered at twice
     * its longest name while the figures stayed cramped. Excluding every
     * identity column instead left the date at `Sep 26` beside times at more
     * than double it, which reads as a column that failed rather than one that
     * fits. A name is as wide as the name; everything else shares alike, and
     * the headings wrap into whatever that leaves.
     */
    const width = indices.map((i) => want[i])
    // The row's name, where it has one — never more than the first column.
    const named = Math.min(1, identity)
    const shared = indices.length - named
    const spare = pageWidthPx - width.reduce((a, b) => a + b, 0)
    if (spare > 0 && shared > 0) {
      const each = Math.floor(spare / shared)
      for (let n = named; n < width.length; n++) width[n] += each
      // The rounding remainder, so the row ends exactly on the margin.
      width[width.length - 1] += spare - each * shared
    }

    const makePage = () => {
      const page = document.createElement('div')
      page.className = 'ytable__page'
      if (masthead) {
        const head = masthead.cloneNode(true) as HTMLElement
        page.appendChild(head)
      }
      const sheet = document.createElement('table')
      sheet.className = 'ytable__grid ytable__sheet'
      // Cloned colgroup and thead, so the header repeats on screen and on paper
      // identically rather than relying on the print engine to do it.
      const group = document.createElement('colgroup')
      const headRow = document.createElement('tr')
      headRow.className = 'yhead'
      indices.forEach((i, n) => {
        const col = document.createElement('col')
        col.className = `ycol ycol--${i < identity ? 'id' : 'line'}`
        col.style.width = `${width[n]}px`
        group.appendChild(col)
        headRow.appendChild(headCells[i].cloneNode(true))
      })
      const thead = document.createElement('thead')
      thead.appendChild(headRow)
      const tbody = document.createElement('tbody')
      sheet.appendChild(group)
      sheet.appendChild(thead)
      sheet.appendChild(tbody)
      page.appendChild(sheet)
      pages.appendChild(page)
      pageCount++
      /*
       * How much room is left, measured ON THIS PAGE rather than computed from
       * probes of its parts. Everything above the rows — the masthead and the
       * header — is in the document by now, so what remains is arithmetic on
       * boxes that actually exist. A probe of a part is a guess about the whole,
       * and this is what that guess got wrong: the page took three rows more
       * than fitted and overflowed them through its own bottom margin, which
       * `overflow: hidden` does not cut because it clips at the BORDER box.
       */
      const box = page.getBoundingClientRect()
      const ps = getComputedStyle(page)
      const usable =
        box.height -
        Number.parseFloat(ps.paddingTop) -
        Number.parseFloat(ps.paddingBottom)
      const chrome =
        (masthead
          ? (
              page.querySelector('.ytable__masthead') as HTMLElement
            ).getBoundingClientRect().height
          : 0) + headRow.getBoundingClientRect().height
      return { tbody, limit: usable - chrome }
    }

    let current = makePage()
    let used = 0

    for (let r = 0; r < bodyRows.length; r++) {
      const source = bodyRows[r]
      const h = rowHeight
      if (used > 0 && used + h > current.limit) {
        current = makePage()
        used = 0
      }
      const tr = document.createElement('tr')
      tr.className = source.className
      const cells = Array.from(source.children) as HTMLElement[]
      for (const i of indices) {
        if (cells[i]) tr.appendChild(cells[i].cloneNode(true))
      }
      current.tbody.appendChild(tr)
      used += h
    }
  }

  flow.remove()
  return pageCount
}
